import { omit } from 'lodash';

import {
  Dimension1,
  POSTING_GET_DATA,
  POSTING_SOURCE_IDS,
  POSTING_TOP_ROLES_GET_DATA,
  type PostingFilters,
  type PostingsDataQuery,
  type PostingsDataQueryVariables,
  type PostingsRolesDataQuery,
  type PostingsRolesDataQueryVariables,
} from '@revelio/data-access';

import { useIsTaxonomyResolved } from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { usePrimaryFilterState } from '../../filters/hooks/usePrimaryFilterState';
import type { PrimaryFilterId } from '../../filters/types/filters';
import { hasPrimaryFilter } from '../../filters/utils/common';
import { getDim1 } from '../../filters/utils/getDim1';
import { getPostingFilters } from '../../filters/utils/getPostingFilters';
import { useChart } from '../Chart/ChartProvider';
import { usePageConfig } from '../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../PageConfigProvider/types';
import type {
  PostingChartType,
  PrimaryEntity,
} from '../PageConfigProvider/types/chart';
import type { PostingSecondaryDimension } from '../PageConfigProvider/types/dimension';
import type { PostingMetricValues } from '../PageConfigProvider/types/metric';
import { usePageState } from '../PageState/PageStateProvider';
import { sanitiseMainPostingData } from './sanitiseMainPostingData';
import type {
  BreakdownSingleDataSet,
  DataProviderChildren,
  SingleDataSet,
  TimeseriesDataSet,
} from './types';
import { convertPostingBreakdown } from './utils/convert-posting-breakdown';
import { convertPostingData } from './utils/convert-posting-data';
import { convertPostingTopData } from './utils/convert-posting-top-data';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';
import {
  buildPrimaryOrderIndex,
  sortByIndex,
} from './utils/sort-by-primary-order';

// Narrow filters to the single entity selected in the Primary Entity switcher
const getOverriddenPrimaryPostingsVariables = ({
  baseVariables,
  metric,
  selectedEntity,
}: {
  baseVariables: PostingsDataQueryVariables;
  metric: PostingMetricValues;
  selectedEntity?: PrimaryEntity;
}): PostingsDataQueryVariables => {
  if (!(metric === 'postings_volume' && selectedEntity?.id)) {
    return baseVariables;
  }

  const selectedId = selectedEntity.id;
  const selectedLevel = selectedEntity.levelId;

  const baseFilters = (baseVariables.filters ?? {}) as PostingFilters;

  switch (baseVariables.dim1) {
    case Dimension1.Company: {
      if (selectedLevel === 'company') {
        const trimmedCompany = Array.isArray(baseFilters.company)
          ? baseFilters.company.filter(
              (id) => Number(id ?? '') === Number(selectedId)
            )
          : baseFilters.company;

        const newFilters = omit({ ...baseFilters, company: trimmedCompany }, [
          'rics_k400',
          'rics_k10',
        ]) as PostingFilters;

        return { ...baseVariables, filters: newFilters };
      }

      if (selectedLevel === 'rics_k10') {
        const trimmedK10 = Array.isArray(baseFilters.rics_k10)
          ? baseFilters.rics_k10.filter(
              (id) => Number(id ?? '') === Number(selectedId)
            )
          : baseFilters.rics_k10;

        const newFilters = omit({ ...baseFilters, rics_k10: trimmedK10 }, [
          'company',
          'rics_k400',
        ]) as PostingFilters;

        return { ...baseVariables, filters: newFilters };
      }

      if (selectedLevel === 'rics_k400') {
        const trimmedK400 = Array.isArray(baseFilters.rics_k400)
          ? baseFilters.rics_k400.filter(
              (id) => Number(id ?? '') === Number(selectedId)
            )
          : baseFilters.rics_k400;

        const newFilters = omit({ ...baseFilters, rics_k400: trimmedK400 }, [
          'company',
          'rics_k10',
        ]) as PostingFilters;

        return { ...baseVariables, filters: newFilters };
      }

      return baseVariables;
    }
    case Dimension1.Geography: {
      const trimmed = Array.isArray(baseFilters.msa)
        ? baseFilters.msa.filter(
            (id) => Number(id ?? '') === Number(selectedId)
          )
        : baseFilters.msa;

      return {
        ...baseVariables,
        filters: { ...baseFilters, msa: trimmed },
      };
    }
    case Dimension1.Role: {
      const roleObj = baseFilters.custom_role;
      const newCustomRole = roleObj?.entities
        ? {
            ...roleObj,
            entities: roleObj.entities.filter(
              (e) => e && e.id === selectedId && e.levelId === selectedLevel
            ),
          }
        : roleObj;

      // Prevent queries with empty entities array
      if (newCustomRole?.entities && newCustomRole.entities.length === 0) {
        return baseVariables;
      }

      return {
        ...baseVariables,
        filters: { ...baseFilters, custom_role: newCustomRole },
      };
    }
    default:
      return baseVariables;
  }
};

export type PostingData =
  | SingleDataSet
  | TimeseriesDataSet
  | BreakdownSingleDataSet;

export const PostingDataProvider = ({
  primaryDimension,
  secondaryDimension,
  chartType,
  metric,
  isSnapshot = false,
  exchangeRate,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension?: PostingSecondaryDimension;
  chartType: PostingChartType;
  metric: PostingMetricValues;
  isSnapshot?: boolean;
  exchangeRate?: number;
  children: DataProviderChildren<PostingData | undefined>;
}) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'week' });
  const isPrimaryFilterSet = hasPrimaryFilter({
    primaryDimension,
    filters: effectiveFilters,
  });

  const { item } = useChart();
  const { pageState } = usePageState();
  const selectedPrimaryEntity = pageState.primaryEntities?.[item?.id];
  const selectedPrimaryEntityName = selectedPrimaryEntity?.name;
  const selectedPrimaryEntityId = selectedPrimaryEntity?.id;

  const { config } = usePageConfig();
  const revelioPageId = config.revelioPageId;

  const defaultMetricMode = (() => {
    if (
      revelioPageId === RevelioPageId.COMPANY_OVERVIEW_V2 ||
      revelioPageId === RevelioPageId.COMPANY_POSTINGS ||
      revelioPageId === RevelioPageId.GEOGRAPHY_POSTINGS ||
      revelioPageId === RevelioPageId.ROLE_POSTINGS ||
      revelioPageId === RevelioPageId.COMPANY_PAY ||
      revelioPageId === RevelioPageId.GEOGRAPHY_PAY ||
      revelioPageId === RevelioPageId.ROLE_PAY
    ) {
      return 'posting';
    }
    return 'expected_hires';
  })();

  const baseVariables = useGetPostingVariables({
    primaryDimension,
    isSnapshot,
    defaultMetricMode,
  });

  const variables = getOverriddenPrimaryPostingsVariables({
    baseVariables,
    metric,
    selectedEntity: selectedPrimaryEntity,
  });

  const { primaryFilter: primaryFilterState } =
    usePrimaryFilterState(primaryDimension);

  const activePrimaryEntityCount =
    primaryFilterState?.primarySelections?.filter((s) => s.isActive).length ??
    0;

  const primarySelections = primaryFilterState?.primarySelections;
  const primaryOrderIndex = buildPrimaryOrderIndex(primarySelections);

  if (!isPrimaryFilterSet) return null;

  if (!variables.filters?.start_date || !variables.filters?.end_date) {
    return null;
  }

  // Use specialised Top-N endpoint only for Role breakdown
  if (secondaryDimension) {
    return (
      <PostingTopQuery variables={variables}>
        {({ data, status }) => {
          const sortedData = data?.posting
            ? sortByIndex(data?.posting, primaryOrderIndex)
            : data?.posting;
          return children({
            data: convertPostingTopData({
              data: sortedData,
            }),
            status,
          });
        }}
      </PostingTopQuery>
    );
  }

  if (item?.plot?.chartType === 'table') {
    return (
      <PostingQuery variables={variables}>
        {({ data, status }) =>
          children({
            data: convertPostingBreakdown({ data: data?.posting, metric }),
            status,
          })
        }
      </PostingQuery>
    );
  }

  return (
    <PostingQuery variables={variables}>
      {({ data, status }) => {
        return children({
          data: data?.posting
            ? convertPostingData({
                metric,
                chartType,
                data:
                  sortByIndex(data.posting, primaryOrderIndex) ?? data.posting,
                activePrimaryEntityCount,
                selectedPrimaryEntityName,
                selectedPrimaryEntityId,
                exchangeRate,
                primaryDimension,
              })
            : undefined,
          status,
        });
      }}
    </PostingQuery>
  );
};

export const PostingDataProviderRaw = ({
  primaryDimension,
  defaultMetricMode,
  isSnapshot = false,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  defaultMetricMode?: 'posting' | 'expected_hires';
  isSnapshot?: boolean;
  children: DataProviderChildren<PostingsDataQuery | undefined>;
}) => {
  const variables = useGetPostingVariables({
    primaryDimension,
    defaultMetricMode,
    isSnapshot,
  });

  return (
    <PostingQuery variables={variables}>
      {({ data, status }) => {
        return children({ data, status });
      }}
    </PostingQuery>
  );
};

const useGetPostingVariables = ({
  primaryDimension,
  defaultMetricMode,
  isSnapshot = false,
}: {
  primaryDimension: PrimaryFilterId;
  defaultMetricMode?: 'posting' | 'expected_hires';
  isSnapshot?: boolean;
}): PostingsDataQueryVariables => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'week' });

  const postingFilters = getPostingFilters(primaryDimension, effectiveFilters, {
    isSnapshot,
    defaultMetricMode,
  });
  const dim1 = getDim1(primaryDimension);

  return {
    dim1,
    filters: {
      ...postingFilters,
      ...(!postingFilters.provider && {
        provider: [POSTING_SOURCE_IDS.unified],
      }),
      ...(!postingFilters.metric_mode && {
        metric_mode: defaultMetricMode,
      }),
    },
  };
};

const PostingQuery = ({
  variables,
  children,
}: {
  variables: PostingsDataQueryVariables;
  children: DataProviderChildren<PostingsDataQuery | undefined>;
}) => {
  const isTaxonomyResolved = useIsTaxonomyResolved();
  const [{ data, status }] = useUrqlQueryWithStatus<PostingsDataQuery>({
    query: POSTING_GET_DATA,
    variables,
    pause: !isTaxonomyResolved,
  });

  const sanitizedData =
    data?.posting?.map((p) => sanitiseMainPostingData(p)) || [];
  const sanitizedDataWithSufficientData = sanitizedData.map((p, index) =>
    (p?.category?.length || 0) > 1
      ? p
      : (data?.posting?.[index] as NonNullable<
          PostingsDataQuery['posting']
        >[number])
  );

  return children({
    data: { posting: sanitizedDataWithSufficientData },
    status,
  });
};

const PostingTopQuery = ({
  variables,
  children,
}: {
  variables: PostingsRolesDataQueryVariables;
  children: DataProviderChildren<PostingsRolesDataQuery | undefined>;
}) => {
  const [{ data, status }] = useUrqlQueryWithStatus<PostingsRolesDataQuery>({
    query: POSTING_TOP_ROLES_GET_DATA,
    variables,
  });

  return children({ data, status });
};
